import axios from 'axios';
import Vue from 'vue';

import { authSetJwt } from '@/store/actions/auth.js';
import i18n from '@/i18n/index.js';
import { loaderFinished, loaderStarted } from '@/store/actions/loader.js';
import router from '@/router/index.js';
import storeFactory from '@/store/index.js';

let cachedClient = null;

const get = () => {
    if (cachedClient === null) {
        cachedClient = createClient();
    }

    return cachedClient;
};

const createClient = () => {
    const client = axios.create();
    client.defaults.headers.common.Accept = 'application/json';
    client.defaults.headers.post['Content-Type'] = 'application/json';

    client.interceptors.request.use((config) => {
        const store = storeFactory.get();
        store.dispatch(loaderStarted);

        if (store.state.auth.jwt) {
            config.headers.authorization = `Bearer ${store.state.auth.jwt}`;
        }

        return config;
    }, (err) => {
        console.error(err);
        const store = storeFactory.get();
        store.dispatch(loaderFinished);
        return Promise.reject(err);
    });

    // Any status within 2xx lies here
    client.interceptors.response.use((resp) => {
        const store = storeFactory.get();
        store.dispatch(loaderFinished);
        return resp;
    }, async (err) => {
        const store = storeFactory.get();

        const logAndExit = () => {
            console.error(err);
            store.dispatch(loaderFinished);
            return Promise.reject(err);
        };

        if (err.response?.status !== 401) {
            return logAndExit();
        }

        const refreshToken = store.state.auth.refreshToken;
        if (!refreshToken) {
            return logAndExit();
        }

        // Do not use this axios instance for the refresh token
        // Should this request fail and we use the same instance,
        // we could be stuck in an infinite loop
        try {
            const response = await axios.post('/api/token/refresh', { refreshToken });
            const tokens = response.data.data;
            store.dispatch(authSetJwt, tokens);
            err.config.headers.authorization = `Bearer ${tokens.accessToken}`;
            const retryResp = await axios.request(err.config);
            store.dispatch(loaderFinished);
            return retryResp;
        } catch (retryError) {
            console.warn('Error retrying after refresh token update');
            console.warn(retryError);
            Vue.$toast.info(i18n.get().t('auth.sessionExpired'));
            router.get().push({ name: 'HomePage' });
            return await logAndExit();
        }
    });

    return client;
};

export default {
    createClient, // exposed for testing only
    get
};
